/**
 * Shared event display utilities for observatory history events.
 * Used by ObservatoryDetailsBase and ObservatoryHistoryViewBase.
 */

/**
 * Get the MUI color for an event type chip.
 * @param {string} eventType - The event type string
 * @returns {string} MUI color: 'error' | 'success' | 'warning' | 'info' | 'default'
 */
export const getEventColor = (eventType) => {
  if (!eventType) return 'default';

  // Explicit checks first (to override pattern matching)
  if (eventType === 'plugin_disconnected') return 'warning';
  if (eventType === 'operational_state_changed') return 'primary';

  // Project history events (issue #883) — explicit so they don't fall through
  // to the generic pattern matching (e.g. files_deleted_from_minio is routine,
  // not an error).
  if (eventType === 'project_created') return 'success';
  if (eventType === 'project_deleted' || eventType === 'member_removed') return 'error';
  if (eventType === 'member_added' || eventType === 'files_uploaded') return 'info';
  if (eventType === 'files_transferred') return 'success';
  if (eventType === 'files_deleted_from_minio') return 'default';
  if (eventType === 'external_storage_added') return 'info';
  if (eventType === 'external_storage_removed') return 'error';

  // Organization history events — explicit mappings.
  if (eventType === 'organization_created') return 'success';
  if (eventType === 'organization_deleted' || eventType === 'member_organization_removed') return 'error';
  if (eventType === 'member_organization_added') return 'info';
  if (eventType === 'report_generated') return 'success';
  if (eventType === 'report_scheduled') return 'info';
  if (eventType === 'subscription_added') return 'success';
  if (eventType === 'subscription_changed') return 'warning';

  // Error states (red)
  if (eventType.includes('error') ||
      eventType.includes('failed') ||
      eventType.includes('aborted') ||
      eventType.includes('cancelled') ||
      eventType === 'fingerprint_mismatch') {
    return 'error';
  }

  // Success states (green)
  if (eventType.includes('completed') ||
      eventType.includes('success') ||
      eventType.includes('connected') ||
      eventType === 'created') {
    return 'success';
  }

  // Warning/change states (orange)
  if (eventType.includes('warning') ||
      eventType.includes('updated') ||
      eventType.includes('changed') ||
      eventType.includes('transferred') ||
      eventType === 'name_changed' ||
      eventType === 'location_updated' ||
      eventType === 'configuration_updated' ||
      eventType === 'server_ui_config_updated' ||
      eventType === 'fingerprint_reset' ||
      eventType === 'storage_limit_reached' ||
      eventType === 'ownership_transferred') {
    return 'warning';
  }

  // Info states (blue)
  if (eventType.includes('started') ||
      eventType.includes('assigned') ||
      eventType.includes('pending') ||
      eventType === 'self_registered' ||
      eventType === 'api_key_generated' ||
      eventType === 'api_key_retrieved' ||
      eventType === 'plugin_software_downloaded' ||
      eventType === 'fingerprint_enrolled' ||
      eventType === 'user_added' ||
      eventType === 'user_removed' ||
      eventType === 'user_role_updated' ||
      eventType === 'organization_added' ||
      eventType === 'organization_removed' ||
      eventType === 'organization_permissions_updated' ||
      eventType === 'weather_history_enabled' ||
      eventType === 'weather_history_disabled' ||
      eventType === 'motd_created' ||
      eventType === 'motd_deactivated' ||
      eventType === 'master_frame_uploaded' ||
      eventType === 'master_archive_uploaded') {
    return 'info';
  }

  return 'default';
};

/**
 * Format event type for display (replaces underscores with spaces and uppercases).
 * @param {string} eventType - The event type string
 * @returns {string} Formatted event type for display
 */
export const formatEventType = (eventType) => {
  if (!eventType) return 'UNKNOWN';
  return eventType.replace(/_/g, ' ').toUpperCase();
};
